import { Router } from "vue-router";
import LayoutContainer from "@/core/views/layouts/base/LayoutContainer.vue";

const createDefaultRouter = (router: Router): void => {
  router.addRoute({
    path: "/",
    component: LayoutContainer,
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "dashboard",
        component: () => import("@/modules/noc/dashboard/views/DashboardHome.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["admin", "noc", "installer", "sales", "financeAdmin", "creditChecker", "cashier"],
          permission: "page.dashboard"
        },
      },
      {
        path: "branches",
        name: "branches",
        component: () => import("@/modules/admin/branches/views/BranchesHome.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["admin", "financeAdmin"],
          permission: "page.branches"
        },
      },
      {
        path: "hotspot-customers",
        name: "hotspotCustomers",
        component: () => import("@/modules/hotspot/customers/views/HotspotCustomersHome.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'cashir', 'financeAdmin'],
          permission: "page.hotspotParents"
        },
      },
      {
        path: "hotspot-customers/:id",
        name: "hotspotCustomerDetails",
        component: () => import("@/modules/hotspot/customers/views/HotspotCustomerDetails.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'cashir', 'financeAdmin'],
          permissions: [
            "page.hotspotParents",
            "page.hotspotAllowances",
            "page.hotspotCreatePackage",
          ],
        },
      },
      {
        path: "hotspot-parents",
        redirect: "/hotspot-customers",
      },
      {
        path: "hotspot-allowances",
        name: "hotspotAllowances",
        component: () => import("@/modules/hotspot/allowances/views/HotspotAllowancesHome.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'cashir', 'financeAdmin'],
          permission: "page.hotspotAllowances"
        },
      },
      {
        path: "hotspot-create-package",
        redirect: "/hotspot-customers",
      },
      {
        path: "customers-link",
        name: "CustomersLink",
        component: () => import("@/modules/sales/customers/views/CustomersLink.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'financeAdmin', 'sales', 'installer'],
          permission: "page.customersLink"
        }
      },
      {
        path: "servary-tickets",
        name: "ServaryTickets",
        component: () => import("@/modules/sales/customers/views/ServaryTicketsList.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'financeAdmin', 'sales', 'noc', 'cashier', 'cashir'],
        }
      },
      {
        path: "tasks",
        name: "installer_tasks",
        component: () => import("@/modules/noc/installer-tasks/views/page.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["admin", "financeAdmin", "installer"],
          permission: "page.tasks"
        },
      },
      {
        path: "tasks/assign",
        name: "taskAssignment",
        component: () => import("@/modules/noc/tasks/views/TaskAssignment.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'noc', 'installer'],
          permission: "page.installations"
        },
      },
      {
        path: "installations-list",
        meta: { requiresAuth: true },
        children: [
          {
            path: "",
            name: "installationsList",
            component: () =>
              import("@/modules/sales/customers/views/CustomersList.vue"),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'cashier', 'cashir', 'sales', 'creditChecker', 'financeAdmin'],
              permission: "page.installationsList"
            },
          },
          {
            path: ":id",
            name: "customerDetails",
            component: () =>
              import("@/modules/sales/customers/views/CustomerDetails.vue"),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'cashier', 'cashir', 'sales', 'creditChecker', 'financeAdmin'],
              // Dedicated details page, or View details from either list
              permissions: [
                "page.customerDetails",
                "action.customer.viewDetails",
                "action.installationsList.viewDetails",
                "page.installationsList"
              ]
            },
          },
          {
            path: "search",
            name: "customersSearch",
            component: () =>
              import("@/modules/sales/customers/views/CustomersSearch.vue"),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'noc', 'sales', 'creditChecker', 'cashier', 'cashir', 'financeAdmin'],
              permission: "page.customerSearch"
            },
          },
          {
            path: "customerForm/:id?",
            name: "customerForm",
            component: () =>
              import("@/modules/sales/customers/views/manage/CustomerForm.vue"),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'cashier', 'cashir', 'sales', 'creditChecker', 'financeAdmin'],
              permission: "page.customerForm"
            },
          },
          {
            path: "customerForm/convert/:id",
            name: "convertOfflineCustomer",
            component: () =>
              import("@/modules/sales/customers/views/manage/ConvertOfflineCustomer.vue"),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'cashier', 'cashir', 'sales', 'creditChecker', 'financeAdmin'],
              permission: "page.customerForm"
            },
          },
        ],
      },
      {
        path: "offline-customers",
        name: "OfflineCustomers",
        component: () => import("@/modules/sales/customers/offline-customers/views/OfflineCustomers.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'cashir', 'sales', 'financeAdmin'],
          permission: "page.offlineCustomers"
        },
      },
      {
        path: "resellers",
        name: "resellers",
        component: () => import("@/modules/sales/resellers/views/ResellersView.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'cashir', 'sales', 'financeAdmin', 'creditChecker'],
          permission: "page.resellers"
        },
      },

      {
        path: "equipment",
        meta: { requiresAuth: true },
        children: [
          {
            path: "list",
            name: "equipmentList",
            component: () =>
              import("@/modules/noc/equipment/views/EquipmentList.vue"),
            meta: {
              requiresAuth: true,
              allowedRoles: ["admin", "sales"],
              permission: "page.equipment"
            },
          },
        ],
      },

      {
        path: "payments/payment/:customerId?",
        name: "payment",
        component: () => import("@/modules/finance/payments/views/ReceivePayment.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["admin", "cashier", "cashir", "financeAdmin", "creditChecker"],
          permission: "page.receivePayment"
        },
      },
      {
        path: "payments/receive/:customerId",
        name: "receivePayment",
        redirect: (to) => ({ name: "payment", params: { customerId: to.params.customerId } }),
        meta: { requiresAuth: true },
      },
      {
        path: "payments/hotspot/:customerId?",
        name: "hotspotPayment",
        component: () => import("@/modules/finance/payments/views/HotspotPayment.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["admin", "cashier", "cashir", "financeAdmin", "creditChecker"],
          permission: "page.hotspotPayments"
        },
      },
      {
        path: "payments",
        name: "payments",
        component: () => import("@/modules/finance/payments/views/PaymentsList.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["admin", "cashier", "cashir", "financeAdmin", "creditChecker"],
          permission: "page.paymentsList"
        },
      },
      {
        path: "payments/queue",
        name: "paymentQueue",
        component: () => import("@/modules/finance/payments/views/PaymentQueueList.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["admin", "cashier", "cashir", "financeAdmin", "creditChecker"],
          permission: "page.paymentQueue"
        },
      },
      {
        path: "payments/cash-ledger",
        name: "cashLedger",
        component: () => import("@/modules/finance/payments/views/CashLedger.vue"),
        meta: {
          title: "Cash Ledger",
          requiresAuth: true,
          allowedRoles: ["admin", "cashier", "cashir", "financeAdmin", "creditChecker"],
          permission: "page.cashLedger"
        },
      },
      {
        path: "payments/cash-books",
        redirect: "/cash-settings?tab=cash-books"
      },
      {
        path: "payments/cash-boxes",
        redirect: "/cash-settings?tab=cash-books"
      },
      {
        path: "billing",
        name: "billing",
        component: () =>
          import("@/modules/finance/billing/views/BillingHome.vue"),
        meta: { 
          requiresAuth: true,
          allowedRoles: ["admin", "financeAdmin", "creditChecker"],
          permission: "page.billing"
        },
      },
      {
        path: "service-discounts",
        name: "serviceDiscounts",
        component: () =>
          import("@/modules/finance/billing/views/ServiceDiscounts.vue"),
        meta: { 
          requiresAuth: true, 
          allowedRoles: ['admin', 'cashier', 'cashir', 'financeAdmin', 'creditChecker', 'sales', 'noc'],
          permission: "page.serviceDiscounts"
        },
      },
      {
        path: "installations",
        meta: { requiresAuth: true },
        children: [
          {
            path: "",
            name: "installations",
            component: () =>
              import("@/modules/noc/installations/views/InstallationsList.vue"),
            meta: {
              requiresAuth: true,
              allowedRoles: ["admin", "noc", "financeAdmin"],
              permission: "page.installations"
            },
          },
          {
            path: "assign",
            name: "installationsAssign",
            component: () =>
              import("@/modules/noc/installations/views/InstallationsAssign.vue"),
            meta: {
              requiresAuth: true,
              allowedRoles: ['admin', 'noc'],
              permission: "page.installations"
            },
          },
          {
            path: "form/:id?",
            name: "installationForm",
            component: () =>
              import("@/modules/noc/installations/views/InstallationFormView.vue"),
            meta: {
              requiresAuth: true,
              // Tasks → installer form; Update/Approve checklist → NOC mode.
              // Legacy keys kept until users are re-saved.
              permissions: [
                "page.tasks",
                "action.installations.editForm",
                "action.noc.approveChecklist",
                "page.installationForm",
              ],
            },
          },
        ],
      },
      {
        path: "users",
        name: "users",
        component: () => import("@/modules/admin/users/views/UsersHome.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ["admin", "noc"],
          permission: "page.users"
        },
      },
      {
        path: "settings",
        name: "settings",
        component: () => import("@/modules/admin/settings/views/SettingsHome.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin'],
          permission: "page.settings"
        },
      },
      {
        path: "/users",
        name: "Users",
        component: () => import("@/modules/admin/users/views/UsersHome.vue"),
        meta: {
          requiresAuth: true,
          layout: "default",
          allowedRoles: ["admin", "noc"],
          permission: "page.users"
        },
      },
      {
        path: "/profile",
        meta: { requiresAuth: true },
        children: [
          {
            path: "",
            name: "Profile",
            component: () => import("@/modules/admin/profile/views/ProfilePage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "reset-password",
            name: "ProfileResetPassword",
            component: () => import("@/modules/admin/profile/views/ResetPassword.vue"),
            meta: { requiresAuth: true },
          }
        ]
      },
      {
        path: "/quickbooks-connect",
        name: "QuickBooksConnect",
        component: () => import("@/modules/finance/quickbooks/views/QuickBooksConnect.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin']
        }
      },
      {
        path: "/quickbooks-admin",
        name: "QuickBooksAdmin",
        component: () => import("@/modules/finance/quickbooks/views/QuickBooksAdmin.vue"),
        meta: { requiresAuth: true, allowedRoles: ['admin'] }
      },
      {
        path: "/quickbooks-sync-review",
        name: "QuickBooksSyncReview",
        component: () => import("@/modules/finance/quickbooks/views/QboSyncUnsyncedReview.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'cashir', 'financeAdmin'],
          permission: "page.qboSyncReview"
        }
      },
      {
        path: "/quickbooks-sync-review-v2",
        name: "QuickBooksSyncReviewV2",
        component: () => import("@/modules/finance/quickbooks/views/QuickbooksSyncReviewV2.vue"),
        meta: {
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'cashir', 'financeAdmin'],
          permission: "page.qboSyncReview"
        }
      },
      {
        path: "/cash-settings",
        name: "CashSettings",
        component: () => import("@/modules/finance/quickbooks/views/CashSettingsHome.vue"),
        meta: {
          title: "Cash & Accounting Settings",
          requiresAuth: true,
          allowedRoles: ['admin', 'cashier', 'cashir', 'financeAdmin'],
          permission: "page.cashSettings"
        }
      },
      {
        path: "/quickbooks-accounts",
        redirect: "/cash-settings?tab=qbo-accounts"
      },
      {
        path: "/quickbooks-payees",
        redirect: "/cash-settings?tab=payees"
      },
      {
        path: "/payees",
        redirect: "/cash-settings?tab=payees"
      },
      {
        path: "/cash-books",
        redirect: "/cash-settings?tab=cash-books"
      },
      {
        path: "/cash-boxes",
        redirect: "/cash-settings?tab=cash-books"
      },
    ],
  });

  // Add callback route as a separate top-level route (no auth required)
  router.addRoute({
    path: "/callback",
    name: "QuickBooksCallback",
    component: () => import("@/modules/finance/quickbooks/views/QuickBooksCallback.vue"),
    meta: {
      requiresAuth: false
    }
  });

  // Public Servary QR page — no sidebar / no token
  router.addRoute({
    path: "/servary/:linkId",
    name: "Servary",
    component: () => import("@/modules/public/servary/views/ServaryPage.vue"),
    meta: {
      requiresAuth: false
    }
  });

  // Short public URL for feedback.farsroute.com/{ObjectId}
  // (also works on CRM host for 24-char hex ids)
  router.addRoute({
    path: "/:linkId([a-fA-F0-9]{24})",
    name: "FeedbackPublic",
    component: () => import("@/modules/public/servary/views/ServaryPage.vue"),
    meta: {
      requiresAuth: false
    }
  });

  // feedback.farsroute.com/ (and any blocked CRM path) → public landing
  router.addRoute({
    path: "/feedback-home",
    name: "FeedbackHome",
    component: () => import("@/modules/public/servary/views/FeedbackHome.vue"),
    meta: {
      requiresAuth: false
    }
  });

  createHttpRouter(router);
  createUserRouter(router);
};

/**
 * Create an HTTP router with a route for common errors.
 *
 * @param {Router} router - the router to add the route to
 * @return {void}
 */
const createHttpRouter = (router: Router): void => {
  router.addRoute({
    path: "/403",
    name: "forbidden",
    component: () => import("@/core/views/common/error/Forbidden.vue"),
  });

  router.addRoute({
    path: "/:pathMatch(.*)*",
    name: "notFound",
    component: () => import("@/core/views/common/error/NotFound.vue"),
  });
};

/**
 * Creates a user router.
 *
 * @param {Router} router - the router to add the user routes to
 * @return {void}
 */
const createUserRouter = (router: Router): void => {
  router.addRoute({
    path: "/user",
    name: "user",
    meta: { requiresAuth: true },
    children: [
      {
        name: "forgot",
        path: "forgot",
        meta: { requiresAuth: true },
        children: [
          {
            name: "password",
            path: "password",
            component: () => import("@/core/auth/views/AuthForgotPassword.vue"),
            meta: { requiresAuth: true },
          },
        ],
      },
    ],
  });
};

export { createDefaultRouter };
